/**
 * gdrive_search - AI Tool for Searching Google Drive
 * On-the-fly file search without entity creation
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('GDRIVE_SEARCH', { enabled: false });

export const gdrive_search: SimpleDocCommand = {
  id: 'gdrive_search',
  logicalName: 'gdrive_search',
  version: 'v1',
  
  description: `Search for files in the user's Google Drive. Returns file metadata without creating entities.

**SEARCH PARAMETERS:**

Full-text search:
{ "query": "budget spreadsheet" }

By file type:
{ "query": "Q4 report", "mimeType": "application/vnd.google-apps.document" }

In specific folder:
{ "query": "invoice", "folderId": "0ABC...xyz" }

Recent files (modified after):
{ "query": "presentation", "modifiedAfter": "2026-02-01" }

Files modified in date range:
{ "query": "notes", "modifiedAfter": "2026-01-01", "modifiedBefore": "2026-01-31" }

Only owned files:
{ "query": "contract", "ownedByMe": true }

Shared with me:
{ "query": "team docs", "sharedWithMe": true }

Starred files:
{ "query": "important", "starred": true }

Sorted by date:
{ "query": "reports", "sortBy": "modifiedTime", "sortDirection": "desc" }

**FILE TYPES (mimeType values):**
- Google Docs: application/vnd.google-apps.document
- Google Sheets: application/vnd.google-apps.spreadsheet
- Google Slides: application/vnd.google-apps.presentation
- Google Forms: application/vnd.google-apps.form
- PDFs: application/pdf
- Folders: application/vnd.google-apps.folder

**DATE FILTERS:**
- modifiedAfter, modifiedBefore: Filter by modification date
- createdAfter, createdBefore: Filter by creation date
- Format: ISO 8601 (YYYY-MM-DD or full ISO date string)

**OWNERSHIP FILTERS:**
- ownedByMe: Only files owned by user
- sharedWithMe: Only files shared with user

**SORT OPTIONS:**
- sortBy: createdTime, modifiedTime, name, starred, viewedByMeTime
- sortDirection: asc (ascending) or desc (descending, default)

**LIMITS:** Max 100 results per query (default 20).

**NOTE:** This tool searches Google Drive directly without creating entities. To read the full content of a file, follow up with gdrive_read using the file ID from these results.`,

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query (searches file names and content)',
        required: true,
        examples: [
          'budget spreadsheet',
          'investor presentation',
          'Q4 report',
          'meeting notes'
        ]
      },
      {
        name: 'mimeType',
        type: 'string',
        description: 'Filter by file type (MIME type)',
        required: false,
        examples: [
          'application/vnd.google-apps.document',
          'application/vnd.google-apps.spreadsheet',
          'application/pdf'
        ]
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Search within specific folder (Drive folder ID)',
        required: false
      },
      {
        name: 'modifiedAfter',
        type: 'string',
        description: 'Only files modified after this date (ISO 8601: YYYY-MM-DD)',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return 'Invalid date format. Use ISO 8601 (YYYY-MM-DD)';
            }
            return null;
          } catch (e: any) {
            return `Invalid date: ${e.message}`;
          }
        }
      },
      {
        name: 'modifiedBefore',
        type: 'string',
        description: 'Only files modified before this date (ISO 8601: YYYY-MM-DD)',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return 'Invalid date format. Use ISO 8601 (YYYY-MM-DD)';
            }
            return null;
          } catch (e: any) {
            return `Invalid date: ${e.message}`;
          }
        }
      },
      {
        name: 'createdAfter',
        type: 'string',
        description: 'Only files created after this date (ISO 8601: YYYY-MM-DD)',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return 'Invalid date format. Use ISO 8601 (YYYY-MM-DD)';
            }
            return null;
          } catch (e: any) {
            return `Invalid date: ${e.message}`;
          }
        }
      },
      {
        name: 'createdBefore',
        type: 'string',
        description: 'Only files created before this date (ISO 8601: YYYY-MM-DD)',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return 'Invalid date format. Use ISO 8601 (YYYY-MM-DD)';
            }
            return null;
          } catch (e: any) {
            return `Invalid date: ${e.message}`;
          }
        }
      },
      {
        name: 'ownedByMe',
        type: 'boolean',
        description: 'Only show files owned by user',
        required: false
      },
      {
        name: 'sharedWithMe',
        type: 'boolean',
        description: 'Only show files shared with user',
        required: false
      },
      {
        name: 'starred',
        type: 'boolean',
        description: 'Only show starred files',
        required: false
      },
      {
        name: 'sortBy',
        type: 'string',
        description: 'Sort results by field: createdTime, modifiedTime, name, starred',
        required: false,
        examples: ['modifiedTime', 'createdTime', 'name', 'starred'],
        validator: (value: string) => {
          if (!value) return null;
          const validFields = ['createdTime', 'modifiedTime', 'name', 'quotaBytesUsed', 'recency', 'starred', 'viewedByMeTime'];
          if (!validFields.includes(value)) {
            return `sortBy must be one of: ${validFields.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'sortDirection',
        type: 'string',
        description: 'Sort direction: asc (ascending) or desc (descending)',
        required: false,
        validator: (value: string) => {
          if (!value) return null;
          if (!['asc', 'desc'].includes(value)) {
            return 'sortDirection must be "asc" or "desc"';
          }
          return null;
        }
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Max results to return (default 20, max 100)',
        required: false,
        validator: (value: number) => {
          if (value && (value < 1 || value > 100)) {
            return 'limit must be between 1 and 100';
          }
          return null;
        }
      }
    ]
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        
        return {
          type: 'gdrive_search_results',
          summary: result.message || `Found ${data.totalCount || 0} files`,
          data: {
            files: data.results || [],
            count: data.totalCount || 0,
            query: toolInput?.query,
            filters: toolInput,
            integrationId: data.integrationId,  // ✅ FIX BUG #15: Pass integrationId to renderer
            spaceId: data.spaceId  // ✅ FIX BUG #16: Pass spaceId to renderer
          },
          meta: {
            searchParams: {
              query: toolInput?.query,
              mimeType: toolInput?.mimeType,
              limit: toolInput?.limit || 20
            }
          }
        };
      }
      
      return {
        type: 'gdrive_search_results',
        summary: result?.message || 'Search completed',
        data: { files: [], count: 0 }
      };
    } catch (error) {
      console.warn('[gdrive_search] Error formatting view data:', error);
      return {
        type: 'gdrive_search_results',
        summary: 'Search completed',
        data: { files: [], count: 0 }
      };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      log.log('[gdrive_search] Searching Drive:', params);
      
      // Get user's Google Drive integration
      const { default: useEntityStore } = await import('../../stores/entityStore');
      const entities = useEntityStore.getState().entities;
      
      const driveIntegrations = entities.filter((e: any) =>
        (e.entityType === 'Integration.GoogleDrive' || e.entityType === 'Integration') &&
        e.skeleton?.integrationType === 'GoogleDrive' &&
        e.skeleton?.status === 'connected' &&
        !e.trashed
      );
      
      if (driveIntegrations.length === 0) {
        return createErrorResult(
          'Google Drive not connected',
          'No connected Google Drive integration found. Please connect Google Drive in Settings → Integrations first.',
          'validation',
          {
            metadata: {
              executionTime: Date.now() - startTime
            }
          }
        );
      }
      
      const integration = driveIntegrations[0];
      const integrationId = integration._id;
      
      log.log('[gdrive_search] Using integration:', integrationId);
      
      // Get current space ID for import operations
      const { default: useSpaceStore } = await import('../../stores/spaceStore');
      const spaceId = useSpaceStore.getState().activeSpace?.id;
      
      log.log('[gdrive_search] Current space:', spaceId);
      
      // Call Google Drive search endpoint
      const { googleDriveAdapter } = await import('../../services/integrations/adapters/GoogleDriveAdapter');
      
      const filters: any = {
        limit: params.limit || 20
      };
      
      if (params.mimeType) filters.mimeType = params.mimeType;
      if (params.folderId) filters.folderId = params.folderId;
      if (params.modifiedAfter) filters.modifiedAfter = params.modifiedAfter;
      if (params.modifiedBefore) filters.modifiedBefore = params.modifiedBefore;
      if (params.createdAfter) filters.createdAfter = params.createdAfter;
      if (params.createdBefore) filters.createdBefore = params.createdBefore;
      if (params.ownedByMe !== undefined) filters.ownedByMe = params.ownedByMe;
      if (params.sharedWithMe !== undefined) filters.sharedWithMe = params.sharedWithMe;
      if (params.starred !== undefined) filters.starred = params.starred;
      if (params.sortBy) filters.sortBy = params.sortBy;
      if (params.sortDirection) filters.sortDirection = params.sortDirection;
      
      const searchResult = await googleDriveAdapter.searchFiles(
        integrationId,
        params.query,
        filters
      );
      
      log.log('[gdrive_search] Search complete:', searchResult.totalCount, 'files');
      
      // Format results for display
      const formattedFiles = searchResult.results.map((file: any) => {
        let fileType = 'File';
        if (file.mimeType?.includes('document')) fileType = 'Google Doc';
        else if (file.mimeType?.includes('spreadsheet')) fileType = 'Google Sheet';
        else if (file.mimeType?.includes('presentation')) fileType = 'Google Slides';
        else if (file.mimeType?.includes('form')) fileType = 'Google Form';
        else if (file.mimeType?.includes('folder')) fileType = 'Folder';
        else if (file.mimeType === 'application/pdf') fileType = 'PDF';
        
        return {
          id: file.id,
          name: file.name,
          type: fileType,
          mimeType: file.mimeType,
          modifiedTime: file.modifiedTime,
          webViewLink: file.webViewLink,
          ownedByMe: file.ownedByMe,
          shared: file.shared,
          size: file.size
        };
      });
      
      const message = searchResult.totalCount === 0
        ? `No files found matching "${params.query}"`
        : `Found ${searchResult.totalCount} file${searchResult.totalCount === 1 ? '' : 's'} matching "${params.query}"`;
      
      return createSuccessResult(
        message,
        {
          results: formattedFiles,
          totalCount: searchResult.totalCount,
          query: params.query,
          filters: filters,
          integrationId: integrationId,  // Pass integration ID to renderer
          spaceId: spaceId  // ✅ FIX BUG #16: Pass space ID to renderer
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime
          },
          ui: {
            resultPreview: 'summary',
            refreshContext: false
          }
        }
      );
      
    } catch (error: any) {
      log.error('[gdrive_search] Search failed:', error);
      
      return createErrorResult(
        'Google Drive search failed',
        error.message || 'Unknown error occurred while searching Google Drive',
        'execution',
        {
          data: {
            query: params.query,
            errorDetails: error.details || error.message
          },
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    }
  },

  // Tool metadata
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  examples: {
    good: [
      {
        input: { query: 'budget spreadsheet' },
        expectedOutput: createSuccessResult('Found 3 files', {
          results: [
            { name: '2026 Budget.xlsx', type: 'Google Sheet' }
          ]
        }),
        description: 'Search for spreadsheets',
        context: 'When AI needs to find financial documents'
      },
      {
        input: { 
          query: 'meeting notes',
          modifiedAfter: '2026-02-01',
          ownedByMe: true
        },
        expectedOutput: createSuccessResult('Found 5 files', {
          results: []
        }),
        description: 'Search recent files owned by user',
        context: 'Finding documents from recent meetings'
      }
    ],
    bad: [
      {
        input: { query: '' },
        error: 'query is required',
        why: 'Empty search query',
        fix: 'Provide a non-empty search query'
      }
    ]
  },

  performance: {
    expectedDuration: 1000, // Google API typically responds in 500-2000ms
    complexity: 'medium'
  }
};
